<template>
  <div
    class="py-4 px-4 custom-page"
  >
    <section>
      <b-row>
        <b-col>
          <BaseHelpTooltip>
            <template v-slot:left>
              <div>
                <h1 class="page-title">
                  Campaign Summary
                </h1>
              </div>
            </template>
            <template v-slot:popover-body>
              <p
                class="mb-0"
              >
                An overview containing the key data highlights from this campaign in a listed format.
              </p>
            </template>
          </BaseHelpTooltip>
        </b-col>
      </b-row>
    </section>
    <section
      class="card px-6 py-5 pb-8"
    >
      <div
        class="manage-highlights-buttons-wrapper"
      >
        <button
          v-if="isManagingHighlights"
          class=" mr-3 btn btn-primary btn-sm"
          @click="updateHighlights()"
          :disabled="haveLimitsBeenExceeded"
        >
          Done
        </button>
        <button
          class="btn btn-secondary btn-sm"
          @click="manageHighlights()"
          v-if="GET_IS_MANAGED_ACCOUNT"
        >
          Manage Highlights
        </button>
      </div>
      <div
        v-if="areHighlightsLoading"
        class="loader h-100 w-100 mx-auto"
        :style="{ 'min-height': isManagingHighlights ? '330.5px' : '176.5px', }"
      />
      <template
        v-else
      >
        <section
          class="content"
        >
          <b-row
            class="justify-content-center mt-2 mb-6"
          >
            <img
              class="w-9"
              src="/images/bulb-icon.png"
              alt="logo"
            >
          </b-row>
          <b-row class="text-right">
            <b-col>
              <span
                class="form-label-small text-muted"
                v-if="isManagingHighlights"
              >
                {{ summary.length }} / {{ summaryMaxLimit }}
              </span>
            </b-col>
          </b-row>
          <b-row class="text-center mt-2">
            <b-col>
              <div
                v-if="isManagingHighlights"
              >
                <b-form-textarea
                  name="summary-input"
                  placeholder="Add a campaign summary"
                  cols="10"
                  rows="2"
                  max-rows="5"
                  :maxlength="summaryMaxLimit"
                  :state="summary.length <= summaryMaxLimit"
                  no-resize
                  v-model="summary"
                />
                <p
                  v-if="summary.length === summaryMaxLimit"
                  class="text-danger mb-0 mt-1 text-sm pl-4"
                >
                  You have reached the maximum number of characters
                </p>
              </div>
              <p
                class="mt-2"
                v-else
              >
                {{ summary }}
              </p>
            </b-col>
          </b-row>
        </section>
        <hr>
        <section>
          <div
            class="content"
          >
            <p
              v-if="isSelectedStatementsOverLimit"
              class="text-danger text-center mb-5"
            >
              You have exceeded the maximum number of {{ maxSelection }} highlights. Please deselect to continue
            </p>
            <div
              v-for="(statements, reportType) in getReportingStatements"
              :key="reportType"
            >
              <div
                v-for="(statement, index) in statements"
                :key="index"
              >
                <div
                  class="d-flex"
                >
                  <label
                    v-if="isManagingHighlights"
                    :id="`statement-switch-label-${statement.id}`"
                    class="m-0 d-flex align-items-start pt-1"
                    :style="{ cursor: isLastSelectedItem(statement) ? 'not-allowed' : 'pointer'}"
                  >
                    <input
                      type="checkbox"
                      :id="`statement-switch-input-${statement.id}`"
                      class="custom-switch-input"
                      :checked="statement.summaryEnabled"
                      @change="toggleStatement(statement, $event.target.checked)"
                      :disabled="isLastSelectedItem(statement)"
                    >
                    <span class="custom-switch-indicator" />
                  </label>
                  <i
                    :class="['mx-4 fa-solid fa-diamond pt-1', `${reportType}-icon-colour`]"
                  />
                  <p>
                    <span
                      v-html="getStatementValue(statement)"
                      class="d-inline-block"
                    />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <hr>
        <section
          v-if="isManagingHighlights || recommendations.length > 0"
          class="content mb-8"
        >
          <h3
            class="text-center"
          >
            Future Recommendations
          </h3>
          <div>
            <div
              v-for="(recommendation, index) in recommendations"
              :class="{ 'mb-5': index !== recommendations.length -1 }"
              :key="index"
              class="w-100 row align-items-baseline mx-0 flex-nowrap"
            >
              <div
                class="mr-3 flex-shrink-0"
              >
                <img
                  class=""
                  src="/images/recommendation-bullet.png"
                  alt="logo"
                  width="28px"
                  height="28px"
                >
              </div>
              <div
                class="flex-grow-1 align-items-baseline"
              >
                <div
                  v-if="isManagingHighlights"
                  class="row align-items-baseline mx-0"
                >
                  <b-form-group
                    class="flex-grow-1 mb-0"
                    :id="`recommendation-${index}`"
                  >
                    <template
                      slot="label"
                      :label-for="`recommendation-input-${index}`"
                      class="form-label"
                    >
                      <span class="form-label-small text-muted">
                        {{ recommendation.length }} / {{ recommendationMaxLimit }}
                      </span>
                    </template>
                    <b-form-input
                      :id="`recommendation-input-${index}`"
                      :ref="`recommendation-input-${index}`"
                      :value="recommendation"
                      @input="updateRecommendation(index, $event)"
                      :maxlength="recommendationMaxLimit"
                      trim
                    />
                    <p
                      v-if="recommendation.length === recommendationMaxLimit"
                      class="text-danger mb-0 mt-1 text-sm pl-4"
                    >
                      You have reached the maximum number of characters
                    </p>
                  </b-form-group>
                  <div>
                    <button
                      class="btn btn-sm border-0 bg-transparent shadow-none"
                      type="button"
                      @click="addRecommendation()"
                      :disabled="isAddRecommendationsDisabled(index) || recommendation === ''"
                    >
                      <i class="fa-solid fa-circle-plus fa-2xl" />
                    </button>
                    <button
                      class="btn btn-sm border-0 bg-transparent shadow-none"
                      type="button"
                      @click="deleteRecommendation(index)"
                      :disabled="false"
                    >
                      <i class="fa-light fa-circle-minus fa-2xl" />
                    </button>
                  </div>
                </div>
                <p
                  v-else
                  class="mb-0"
                >
                  {{ recommendation }}
                </p>
              </div>
            </div>
            <p
              v-if="isManagingHighlights && recommendations.length === maxRecommendationsNumber"
              id="recommendations-error-message"
              class="mb-0 text-right text-info form-label-small mt-1"
            >
              You have reached the max number of recommendations
            </p>
          </div>
        </section>
      </template>
    </section>
  </div>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import * as reportingTypes from '@/modules/reporting/store/types';
import * as accountTypes from '@/modules/account/store/types';

import _ from 'lodash';

export default {
  name: 'CampaignSummary',
  data() {
    return {
      areHighlightsLoading: true,
      isManagingHighlights: false,
      maxSelection: 10,
      fullStatementsList: {},
      summary: null,
      summaryMaxLimit: 250,
      summaryEnabledIds: [],
      maxRecommendationsNumber: 3,
      recommendations: [],
      recommendationMaxLimit: 150,
    };
  },
  computed: {
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('reporting', [
      'campaignReportConfiguration',
    ]),
    isSelectedStatementsOverLimit() {
      return this.summaryEnabledIds.length > this.maxSelection;
    },
    getReportingStatements() {
      if (this.areHighlightsLoading) return {};
      return this.isManagingHighlights ? this.getAllEnabledStatements() : this.getSummaryEnabledStatements();
    },
    isSummaryOverLimit() {
      return this.summary.length > 250;
    },
    areRecommendationOverLimit() {
      return this.recommendations.some((recommendation) => recommendation.length > 150);
    },
    haveLimitsBeenExceeded() {
      return this.isSelectedStatementsOverLimit || this.isSummaryOverLimit || this.areRecommendationOverLimit;
    },
  },
  methods: {
    ...mapActions('reporting', [
      reportingTypes.FETCH_REPORT_STATEMENTS_FOR_TYPE,
      reportingTypes.UPDATE_SUMMARY_ENABLED_STATEMENTS,
      reportingTypes.UPDATE_CAMPAIGN_REPORT_CONFIGURATION,
    ]),
    manageHighlights() {
      this.isManagingHighlights = true;
      if (!this.recommendations.length) this.addRecommendation();
    },
    getAllEnabledStatements() {
      const enabledStatements = {};
      for (const reportType in this.fullStatementsList) {
        enabledStatements[reportType] = [];
        for (const statement of this.fullStatementsList[reportType]) {
          if (statement.enabled) enabledStatements[reportType].push(statement);
        }
      }
      return enabledStatements;
    },
    getSummaryEnabledStatements() {
      const summaryEnabledStatements = {};
      for (const reportType in this.fullStatementsList) {
        summaryEnabledStatements[reportType] = [];
        for (const statement of this.fullStatementsList[reportType]) {
          if (statement.summaryEnabled) summaryEnabledStatements[reportType].push(statement);
        }
      }
      return summaryEnabledStatements;
    },
    async updateHighlights() {
      this.isManagingHighlights = false;
      if (!this.summaryEnabledIds.length) return;
      this.areHighlightsLoading = true;
      try {
        await this.UPDATE_SUMMARY_ENABLED_STATEMENTS(this.summaryEnabledIds);

        this.recommendations = this.recommendations.filter((recommendation) => recommendation !== '');

        await this.UPDATE_CAMPAIGN_REPORT_CONFIGURATION({
          campaignId: this.campaign.id,
          body: {
            summary: this.summary,
            recommendations: this.recommendations,
          },
        });
      } catch (e) {
        // console.log(e);
      } finally {
        await this.resetData();
        this.areHighlightsLoading = false;
      }
    },
    isStatementWithReplacements(statement) {
      return statement.value.includes('{{') || statement.value.includes('}}');
    },
    trimCurlyBrackets(keyword) {
      return keyword.replace('{{', '').replace('}}', '');
    },
    getStatementValue(statement) {
      if (!this.isStatementWithReplacements(statement)) return statement.value;
      return this.replaceMacrosInStatement(statement, statement.value, 0);
    },
    replaceMacrosInStatement(statement, updatedStatement, searchStartPos) {
      const replacementStartPos = statement.value.indexOf('{{', searchStartPos);
      const replacementEndPos = statement.value.indexOf('}}', searchStartPos) + 2;
      if (replacementStartPos === -1 || replacementEndPos === -1) {
        return updatedStatement;
      }
      const stringToMatch = statement.value.slice(replacementStartPos, replacementEndPos);
      updatedStatement = updatedStatement.replace(stringToMatch, (`${this.getReplacementValue(statement, stringToMatch)}`));
      const index = statement.value.indexOf(stringToMatch, searchStartPos);
      searchStartPos = index + stringToMatch.length;
      return this.replaceMacrosInStatement(statement, updatedStatement, searchStartPos);
    },
    getReplacementKeyword(statement, stringToMatch) {
      return statement.replacements[`${this.trimCurlyBrackets(stringToMatch)}`] ?? '';
    },
    getReplacementValue(statement, stringToMatch) {
      if (this.getReplacementKeyword(statement, stringToMatch) !== '') {
        return `${this.getReplacementKeyword(statement, stringToMatch)}`;
      }
      return '[-]';
    },
    toggleStatement(statement, isChecked) {
      statement.summaryEnabled = isChecked;
      if (isChecked) return this.summaryEnabledIds.push(statement.id);
      this.summaryEnabledIds.splice(this.summaryEnabledIds.indexOf(statement.id), 1);
    },
    isLastSelectedItem(statement) {
      return statement.summaryEnabled && this.summaryEnabledIds.length === 1;
    },
    async populateStatementList() {
      const types = ['CAMPAIGN_OVERVIEW', 'CAMPAIGN_PERFORMANCE', 'CONTENT_PERFORMANCE'];
      await Promise.all(types.map((type) => this.FETCH_REPORT_STATEMENTS_FOR_TYPE(type)))
        .then((response) => {
          const clonedResponse = _.cloneDeep(response);
          for (let index = 0; index < types.length; index++) {
            this.fullStatementsList[types[index]] = [];
            for (const statement of clonedResponse[index]) {
              this.fullStatementsList[types[index]].push(statement);
              if (statement.summaryEnabled) this.summaryEnabledIds.push(statement.id);
            }
          }
        });
    },
    isAddRecommendationsDisabled(index) {
      return this.recommendations.length >= this.maxRecommendationsNumber || index !== this.recommendations.length - 1;
    },
    addRecommendation() {
      this.recommendations.push('');
    },
    updateRecommendation(index, recommendation) {
      this.recommendations.splice(index, 1, recommendation);
    },
    deleteRecommendation(index) {
      this.recommendations.splice(index, 1);
      if (!this.recommendations.length) this.addRecommendation();
    },
    async resetData() {
      this.fullStatementsList = {};
      this.summaryEnabledIds = [];
      await this.populateStatementList();
    },
  },
  async created() {
    try {
      await this.populateStatementList();
      this.recommendations = this.campaignReportConfiguration.recommendations;
      this.summary = this.campaignReportConfiguration.summary;
    } catch (err) {
      this.$router.back();
    } finally {
      this.areHighlightsLoading = false;
    }
  },
};
</script>
<style lang="scss" scoped>
.custom-page {
  min-height: 100%;
}
.manage-highlights-buttons-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
.content {
  width: 75%;
  margin: 0px auto;
}
.text-sm {
  font-weight: 400;
  font-size: 87.5%;
}
</style>
